import { HUB_EVENTS, hubEmitter } from '@/core/realtime';
import type { Conversation, ConversationListResponse } from '@/features/conversation';
import { conversationKeys } from '@/features/conversation/queries/conversation-keys';
import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

export const useConversationRemovedEvent = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleConversationRemoved = (conversationId: string) => {
      if (!conversationId) {
        return;
      }

      // Remove conversation from all matching queries
      queryClient.setQueriesData<InfiniteData<ConversationListResponse>>(
        { queryKey: conversationKeys.all },
        (oldData: any) => {
          if (!oldData?.pages) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page: any) => {
              const hasConversation = page.conversations.some(
                (conv: Conversation) => conv.conversationId === conversationId
              );
              if (!hasConversation) return page;

              // Filter out the removed conversation
              const filteredConversations = page.conversations.filter(
                (conv: Conversation) => conv.conversationId !== conversationId
              );

              return {
                ...page,
                conversations: filteredConversations
              };
            })
          };
        }
      );
    };

    // Register hub event handler
    hubEmitter.on(HUB_EVENTS.MESSAGE.CONVERSATION_REMOVED, handleConversationRemoved);

    // Cleanup
    return () => {
      hubEmitter.off(HUB_EVENTS.MESSAGE.CONVERSATION_REMOVED, handleConversationRemoved);
    };
  }, [queryClient]);
};
